import { Database } from "services/databases/useDatabasesNames";

import { ItemDataType } from "components/Tree/types";
import { TREE_NODE_PADDING } from "components/Tree/utils";

export type SearchRow = {
  tableCatalog: string;
  tableSchema: string;
  tableName: string;
  objectName: string;
  objectType: "table" | "column" | "index";
  dataType?: string | null;
  tableType?: string | null;
  indexType?: string | null;
  indexName?: string | null;
  indexDefinition?: string | null;
};

const getTableKey = (row: SearchRow) =>
  `${row.tableCatalog}::${row.tableSchema}::${row.tableName}`;

const enrichRowsWithTableType = (rows: SearchRow[]): SearchRow[] => {
  const tableTypeLookup = new Map<string, string>();
  rows.forEach(row => {
    if (row.objectType === "table" && row.tableType) {
      tableTypeLookup.set(getTableKey(row), row.tableType);
    }
  });

  return rows.map(row => {
    if (row.objectType !== "table" && !row.tableType) {
      const tableType = tableTypeLookup.get(getTableKey(row));
      if (tableType) {
        return { ...row, tableType };
      }
    }
    return row;
  });
};

type SchemaGroups = {
  tablesNode: ItemDataType;
  externalTablesNode: ItemDataType;
  viewsNode: ItemDataType;
};

const getColumnNodeIndent = (layer: number) => (layer - 2) * TREE_NODE_PADDING;

const resolveTableNodeType = (tableType?: string | null): string => {
  if (!tableType) return "table";
  const normalized = tableType.toUpperCase();
  if (normalized.includes("VIEW")) return "view";
  if (normalized.includes("EXTERNAL")) return "external_table";
  return "table";
};

const createSchemaChildren = (
  databaseName: string,
  schemaName: string
): SchemaGroups => {
  const baseValue = `${databaseName}_${schemaName}`;
  return {
    tablesNode: {
      type: "tables",
      label: "Tables",
      value: `${baseValue}_tables`,
      databaseName,
      children: [],
    },
    externalTablesNode: {
      type: "external_tables",
      label: "External tables",
      value: `${baseValue}_external`,
      databaseName,
      children: [],
    },
    viewsNode: {
      type: "views",
      label: "Views",
      value: `${baseValue}_views`,
      databaseName,
      children: [],
    },
  };
};

const getColumnType = (tableType: string): string => {
  const typeMap: Record<string, string> = {
    information_schema_table: "information_schema_column",
    view: "view_column",
    external_table: "external_table_column",
  };
  return typeMap[tableType] || "column";
};

/**
 * Builds a tree structure from search result rows.
 * Creates hierarchy: Database > Schema > Tables/Views/External > Table > Columns/Indexes
 */
export const buildSearchTree = (
  rows: SearchRow[],
  databases: Database[]
): ItemDataType[] => {
  const enrichedRows = enrichRowsWithTableType(rows);

  // Node caches
  const databaseNodes = new Map<string, ItemDataType>();
  const schemaNodes = new Map<string, ItemDataType>();
  const schemaGroups = new Map<string, SchemaGroups>();
  const infoSchemaNodes = new Map<string, ItemDataType>();
  const infoSchemaViewsNodes = new Map<string, ItemDataType>();
  const tableNodes = new Map<string, ItemDataType>();
  const columnsGroupNodes = new Map<string, ItemDataType>();
  const indexesGroupNodes = new Map<string, ItemDataType>();
  const tableMeta = new Map<string, SearchRow>();

  // Initialize database nodes
  databases.forEach(db => {
    databaseNodes.set(db.catalogName, {
      type: "database",
      label: db.catalogName,
      value: db.catalogName,
      databaseName: db.catalogName,
      payload: db,
      children: [],
    });
  });

  const getOrCreateInfoSchema = (databaseName: string) => {
    if (infoSchemaNodes.has(databaseName)) {
      return infoSchemaViewsNodes.get(databaseName)!;
    }

    const dbNode = databaseNodes.get(databaseName);
    if (!dbNode) return null;

    const infoSchemaNode: ItemDataType = {
      type: "information_schema",
      label: "information_schema",
      value: `${databaseName}_information_schema`,
      databaseName,
      children: [],
    };
    const viewsNode: ItemDataType = {
      type: "information_schema_views",
      label: "Views",
      value: `${databaseName}_information_schema_views`,
      databaseName,
      children: [],
    };
    infoSchemaNode.children = [viewsNode];
    infoSchemaNodes.set(databaseName, infoSchemaNode);
    infoSchemaViewsNodes.set(databaseName, viewsNode);
    (dbNode.children as ItemDataType[]).push(infoSchemaNode);

    return viewsNode;
  };

  const getOrCreateSchemaGroups = (
    databaseName: string,
    schemaName: string
  ): SchemaGroups | null => {
    if (schemaName === "information_schema") {
      const viewsNode = getOrCreateInfoSchema(databaseName);
      if (!viewsNode) return null;
      return {
        tablesNode: viewsNode,
        externalTablesNode: viewsNode,
        viewsNode,
      };
    }

    const key = `${databaseName}::${schemaName}`;
    if (schemaGroups.has(key)) {
      return schemaGroups.get(key)!;
    }

    const dbNode = databaseNodes.get(databaseName);
    if (!dbNode) return null;

    const groups = createSchemaChildren(databaseName, schemaName);
    const schemaNode: ItemDataType = {
      type: "schema",
      label: schemaName,
      value: `${databaseName}_${schemaName}`,
      databaseName,
      children: [
        groups.tablesNode,
        groups.externalTablesNode,
        groups.viewsNode,
      ],
    };

    schemaNodes.set(key, schemaNode);
    schemaGroups.set(key, groups);
    (dbNode.children as ItemDataType[]).push(schemaNode);

    return groups;
  };

  const getOrCreateTable = (row: SearchRow): ItemDataType | null => {
    const key = getTableKey(row);
    if (tableNodes.has(key)) {
      return tableNodes.get(key)!;
    }

    const { tableCatalog, tableSchema, tableName } = row;
    if (!tableCatalog || !tableSchema || !tableName) return null;

    const groups = getOrCreateSchemaGroups(tableCatalog, tableSchema);
    if (!groups) return null;

    const tableType =
      tableSchema === "information_schema"
        ? "information_schema_table"
        : resolveTableNodeType(row.tableType);

    /* eslint-disable no-nested-ternary */
    const parentGroup =
      tableType === "view"
        ? groups.viewsNode
        : tableType === "external_table"
          ? groups.externalTablesNode
          : groups.tablesNode;

    const value = `${parentGroup.value}_${tableName}`;
    const tableNode: ItemDataType = {
      type: tableType,
      label: tableName,
      value,
      name: tableName,
      databaseName: tableCatalog,
      payload: row,
      children: [],
    };

    if (tableType === "table") {
      const columnsNode: ItemDataType = {
        type: "columns",
        label: "Columns",
        value: `${value}_columns`,
        name: tableName,
        databaseName: tableCatalog,
        children: [],
      };
      const indexesNode: ItemDataType = {
        type: "indexes",
        label: "Indexes",
        value: `${value}_indexes`,
        name: tableName,
        databaseName: tableCatalog,
        children: [],
      };
      tableNode.children = [columnsNode, indexesNode];
      columnsGroupNodes.set(key, columnsNode);
      indexesGroupNodes.set(key, indexesNode);
    }

    (parentGroup.children as ItemDataType[]).push(tableNode);
    tableNodes.set(key, tableNode);
    return tableNode;
  };

  const addColumn = (row: SearchRow, tableNode: ItemDataType) => {
    const columnNode: ItemDataType = {
      type: getColumnType(tableNode.type as string),
      label: row.objectName,
      value: `${tableNode.value}_${row.objectName}`,
      databaseName: row.tableCatalog,
      payload: row,
      children: null,
      ...(tableNode.type === "table" && {
        getTreeNodeIndent: getColumnNodeIndent,
      }),
    };

    if (tableNode.type === "table") {
      const columnsNode = columnsGroupNodes.get(getTableKey(row));
      if (columnsNode) {
        (columnsNode.children as ItemDataType[]).push(columnNode);
      }
    } else {
      (tableNode.children as ItemDataType[]).push(columnNode);
    }
  };

  const addIndex = (row: SearchRow) => {
    const indexesNode = indexesGroupNodes.get(getTableKey(row));
    if (!indexesNode) return;

    const indexName = row.indexName || row.objectName;
    (indexesNode.children as ItemDataType[]).push({
      type: "index",
      label: indexName,
      value: `${indexesNode.value}_${indexName}`,
      databaseName: row.tableCatalog,
      payload: row,
      children: null,
      getTreeNodeIndent: getColumnNodeIndent,
    });
  };

  enrichedRows.forEach(row => {
    if (row.objectType === "table") {
      tableMeta.set(getTableKey(row), row);
    }
  });

  tableMeta.forEach(row => getOrCreateTable(row));

  enrichedRows.forEach(row => {
    if (row.objectType === "table") return;

    const tableRow = tableMeta.get(getTableKey(row)) || {
      ...row,
      objectType: "table" as const,
    };
    const tableNode = getOrCreateTable(tableRow);
    if (!tableNode) return;

    if (row.objectType === "column") {
      addColumn(row, tableNode);
    } else if (row.objectType === "index") {
      addIndex(row);
    }
  });

  databaseNodes.forEach(node => {
    if (Array.isArray(node.children) && node.children.length > 1) {
      node.children.sort((left, right) => {
        const leftIsInfo = left.label === "information_schema";
        const rightIsInfo = right.label === "information_schema";
        if (leftIsInfo !== rightIsInfo) {
          return leftIsInfo ? 1 : -1;
        }
        return left.label.localeCompare(right.label);
      });
    }
  });

  return Array.from(databaseNodes.values());
};
